/**
 * DocumentUploadDialog - Document Upload Dialog Component
 * Extracted from ClientDetailPage - matches exact implementation
 */
import { useRef } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Upload, File } from "lucide-react";

const DOCUMENT_TYPES = [
  { value: "intake", label: "Intake Documents" },
  { value: "medical", label: "Medical Records" },
  { value: "authorization", label: "Authorizations" },
  { value: "assessment", label: "Assessments" },
  { value: "admission", label: "Admission Forms" },
  { value: "care_plan", label: "Care Plans" },
  { value: "consent", label: "Consent Forms" },
  { value: "insurance", label: "Insurance Documents" },
  { value: "id", label: "ID/Identification" },
  { value: "other", label: "Other" },
];

export function DocumentUploadDialog({
  open,
  onOpenChange,
  clientName,
  selectedFile,
  handleFileSelect,
  documentType,
  setDocumentType,
  documentNotes,
  setDocumentNotes,
  documentTypes = DOCUMENT_TYPES,
  uploading,
  onUpload,
}) {
  const fileInputRef = useRef(null);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Upload Document</DialogTitle>
          <DialogDescription>
            Upload a clinical document for {clientName}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>File *</Label>
            <div 
              className="border-2 border-dashed border-slate-200 rounded-lg p-6 text-center cursor-pointer hover:border-primary/50 transition-colors"
              onClick={() => fileInputRef.current?.click()}
            >
              <input
                ref={fileInputRef}
                type="file"
                className="hidden"
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif"
                onChange={handleFileSelect}
              />
              {selectedFile ? (
                <div className="flex items-center justify-center gap-2">
                  <File className="w-5 h-5 text-primary" />
                  <span className="text-sm font-medium">{selectedFile.name}</span>
                </div>
              ) : (
                <>
                  <Upload className="w-8 h-8 mx-auto mb-2 text-slate-400" />
                  <p className="text-sm text-slate-600">Click to select a file</p>
                  <p className="text-xs text-slate-400 mt-1">PDF, DOC, DOCX, JPG, PNG (max 10MB)</p>
                </>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="doc-type">Document Type</Label>
            <Select value={documentType} onValueChange={setDocumentType}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {documentTypes.map((type) => (
                  <SelectItem key={type.value} value={type.value}>
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="doc-notes">Notes (Optional)</Label>
            <Textarea
              id="doc-notes"
              placeholder="Add any notes about this document..."
              value={documentNotes}
              onChange={(e) => setDocumentNotes(e.target.value)}
              rows={2}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={onUpload} disabled={uploading || !selectedFile}>
            {uploading ? "Uploading..." : "Upload"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
